import type { Offer, OfferCurrency } from '@/api/types';

export interface GeoRateRange {
  currency: OfferCurrency;
  minCents: number;
  maxCents: number;
  offersWithRate: number;
}

export interface OfferGeoCoverageRow {
  code: string;
  offerCount: number;
  rateRanges: GeoRateRange[];
}

export function buildOfferGeoCoverage(
  offers: Offer[],
  options: { activeOnly?: boolean } = {},
): OfferGeoCoverageRow[] {
  const activeOnly = options.activeOnly ?? true;
  const byGeo = new Map<string, { offerIds: Set<string>; rates: Map<OfferCurrency, number[]> }>();

  for (const offer of offers) {
    if (activeOnly && offer.status !== 'active') continue;

    const codes = new Set(
      offer.geo
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    );

    for (const code of codes) {
      const entry = byGeo.get(code) ?? {
        offerIds: new Set<string>(),
        rates: new Map<OfferCurrency, number[]>(),
      };
      entry.offerIds.add(offer.id);

      if (offer.payout_cents != null) {
        const rates = entry.rates.get(offer.currency) ?? [];
        rates.push(offer.payout_cents);
        entry.rates.set(offer.currency, rates);
      }
      byGeo.set(code, entry);
    }
  }

  return Array.from(byGeo, ([code, entry]) => ({
    code,
    offerCount: entry.offerIds.size,
    rateRanges: Array.from(entry.rates, ([currency, rates]) => ({
      currency,
      minCents: Math.min(...rates),
      maxCents: Math.max(...rates),
      offersWithRate: rates.length,
    })).sort((a, b) => a.currency.localeCompare(b.currency)),
  }));
}
